/**
 * THE SIGN-IN WIRE CONTRACT, and the state machine that walks it.
 *
 * ── The defect this file exists to prevent ───────────────────────────────────
 *
 * `Login.tsx` declared `{ mfaRequired?: boolean; mfaToken?: string }` and branched on
 * `response.mfaRequired === true`. `POST /api/auth/login` has never returned either key.
 * It returns `{ status: 'STEP_REQUIRED', nextStep, stepToken, expiresIn, message }`. So
 * the branch was dead, the handler fell through to `window.location.assign('/')`, the app
 * asked `/api/auth/me`, got 401, and bounced straight back to the sign-in screen. Correct
 * credentials failed with no explanation.
 *
 * That is the `@shared/api` envelope story again: two hand-written descriptions of one
 * payload, in two files, that nothing compared. `api.post<T>` is an unchecked CAST — it
 * does not verify a single key. So this module does not declare an interface and cast to
 * it. `readLoginOutcome` INSPECTS the body and returns `null` when it recognises neither
 * shape, and every caller treats `null` as a hard failure rather than as success. The next
 * time the contract moves, the screen says so instead of silently redirecting to a page
 * the user is not signed in to.
 *
 * ── The actual contract, read from `src/backend/routes/auth.ts` and verified with live
 *    requests against the running MySQL deployment ─────────────────────────────
 *
 *   POST /api/auth/login              { email, password }
 *   POST /api/auth/mfa/enrol/start    { stepToken }                → ENROLMENT_STARTED
 *   POST /api/auth/mfa/enrol/confirm  { stepToken, code }
 *   POST /api/auth/login/mfa          { stepToken, code }
 *   POST /api/auth/password/change    { stepToken, newPassword }
 *
 * Every one of those except `/mfa/enrol/start` answers with the SAME two-way outcome:
 *
 *   { status: 'STEP_REQUIRED', nextStep, stepToken, expiresIn, message }
 *   { status: 'AUTHENTICATED', accessToken, expiresIn, csrfToken, sessionId, user }
 *
 * Note the field is `stepToken` on the way in as well as out — NOT `mfaToken`. The route
 * copies it to `tempToken` internally, but only to give the rate limiter an account
 * dimension (`keyLimiterByStepToken`); it is not part of the request contract.
 *
 * ── Steps chain ──────────────────────────────────────────────────────────────
 *
 * There is no "the MFA step". On a first sign-in with the break-glass account the observed
 * sequence is FOUR steps:
 *
 *   password → MFA_ENROLMENT → PASSWORD_CHANGE → MFA_VERIFICATION → session
 *
 * (The trailing MFA_VERIFICATION is the server re-deriving the whole step list after the
 * forced password change — `continueLogin` calls `nextLoginStep`, which now sees
 * `mfaEnabled: true` and asks for a code again even though enrolment already proved
 * possession one step earlier. Redundant, and reported to `auth-agent`; it terminates
 * because `stepAfterMfa` then finds nothing left to do.) The client must therefore LOOP on
 * whatever the server names next and must never assume a step count or an order.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Steps
// ─────────────────────────────────────────────────────────────────────────────

/** Mirrors `LoginStep` in `src/backend/auth/tokens.ts`. */
export type LoginStep = 'MFA_ENROLMENT' | 'MFA_VERIFICATION' | 'PASSWORD_CHANGE';

const LOGIN_STEPS: readonly LoginStep[] = ['MFA_ENROLMENT', 'MFA_VERIFICATION', 'PASSWORD_CHANGE'];

function isLoginStep(value: unknown): value is LoginStep {
  return typeof value === 'string' && (LOGIN_STEPS as readonly string[]).includes(value);
}

/** Short label for the progress indicator. */
export const STEP_LABEL: Readonly<Record<LoginStep, string>> = {
  MFA_ENROLMENT: 'Set up authenticator',
  MFA_VERIFICATION: 'Two-factor code',
  PASSWORD_CHANGE: 'Choose a new password',
};

// ─────────────────────────────────────────────────────────────────────────────
// Outcomes
// ─────────────────────────────────────────────────────────────────────────────

export interface StepRequired {
  status: 'STEP_REQUIRED';
  nextStep: LoginStep;
  /** Bearer for the NEXT call. Ten-minute life; a fresh one arrives with every step. */
  stepToken: string;
  /** Seconds. 600 at the time of writing. */
  expiresIn: number;
  /** The server's own explanation of the step. Rendered verbatim — it is well written. */
  message: string;
}

export interface SignedIn {
  status: 'AUTHENTICATED';
  /** Also delivered as an httpOnly cookie. Deliberately NOT stored by this client. */
  accessToken: string;
  expiresIn: number;
  csrfToken: string;
  sessionId: string;
  user: { id: string; email: string; displayName: string };
}

export type LoginOutcome = StepRequired | SignedIn;

/** What `POST /api/auth/mfa/enrol/start` returns. The only time any of it is ever shown. */
export interface EnrolmentStarted {
  status: 'ENROLMENT_STARTED';
  /** Base32 shared secret, for manual entry when a camera is unavailable. */
  secret: string;
  /** `otpauth://totp/...` — this is what becomes the QR code. */
  otpauthUri: string;
  /** Eight single-use codes, formatted `XXXX-XXXX-XXXX`. Shown once, never retrievable. */
  recoveryCodes: string[];
  /** REPLACES the step token that was spent starting enrolment. */
  stepToken: string;
  message: string;
}

function record(body: unknown): Record<string, unknown> | null {
  return typeof body === 'object' && body !== null ? (body as Record<string, unknown>) : null;
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

/**
 * Reads a login outcome, or returns `null` when the body is neither shape.
 *
 * `null` is the whole point. A cast would have let the original defect through again; a
 * reader that refuses to guess forces the caller to surface "the server answered something
 * this screen does not understand" instead of pretending the user is signed in.
 */
export function readLoginOutcome(body: unknown): LoginOutcome | null {
  const payload = record(body);
  if (payload === null) return null;

  if (payload['status'] === 'STEP_REQUIRED') {
    const stepToken = text(payload['stepToken']);
    const nextStep = payload['nextStep'];
    if (stepToken === null || !isLoginStep(nextStep)) return null;
    const expiresIn = payload['expiresIn'];
    return {
      status: 'STEP_REQUIRED',
      nextStep,
      stepToken,
      expiresIn: typeof expiresIn === 'number' && Number.isFinite(expiresIn) ? expiresIn : 600,
      message: text(payload['message']) ?? 'One more step is needed before you can be signed in.',
    };
  }

  if (payload['status'] === 'AUTHENTICATED') {
    const user = record(payload['user']) ?? {};
    return {
      status: 'AUTHENTICATED',
      accessToken: text(payload['accessToken']) ?? '',
      expiresIn: typeof payload['expiresIn'] === 'number' ? payload['expiresIn'] : 0,
      csrfToken: text(payload['csrfToken']) ?? '',
      sessionId: text(payload['sessionId']) ?? '',
      user: {
        id: text(user['id']) ?? '',
        email: text(user['email']) ?? '',
        displayName: text(user['displayName']) ?? '',
      },
    };
  }

  return null;
}

/** Same discipline for the enrolment payload: verify, never cast. */
export function readEnrolmentStarted(body: unknown): EnrolmentStarted | null {
  const payload = record(body);
  if (payload === null) return null;
  const secret = text(payload['secret']);
  const otpauthUri = text(payload['otpauthUri']);
  const stepToken = text(payload['stepToken']);
  const codes = payload['recoveryCodes'];
  if (secret === null || otpauthUri === null || stepToken === null) return null;
  if (!Array.isArray(codes) || codes.length === 0 || !codes.every((code) => typeof code === 'string')) {
    return null;
  }
  return {
    status: 'ENROLMENT_STARTED',
    secret,
    otpauthUri,
    stepToken,
    recoveryCodes: codes,
    message: text(payload['message']) ?? '',
  };
}

/**
 * The message shown when the server answers a shape this screen does not recognise.
 *
 * It names the endpoint, because the only useful next action is for somebody to compare
 * this file against `src/backend/routes/auth.ts` — which is exactly the comparison that
 * was never made.
 */
export function unrecognisedOutcome(endpoint: string): string {
  return (
    `Signing in did not fail, but ${endpoint} answered in a form this version of AgentERP does not ` +
    'understand, so you have not been signed in. This is a bug, not something you did — quote this ' +
    'message to an administrator.'
  );
}
